import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DocumentType } from '../../types';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';

interface DocumentTypeFormProps {
  documentType?: DocumentType;
  onSubmit: (data: Partial<DocumentType>) => void;
  onCancel: () => void;
}

const DocumentTypeForm: React.FC<DocumentTypeFormProps> = ({ 
  documentType, 
  onSubmit, 
  onCancel 
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<DocumentType>({
    // resolver: yupResolver(schema),
    defaultValues: documentType || {
      isRequired: true,
      notificationDays: 30
    }
  });

  useEffect(() => {
    if (documentType) {
      reset(documentType);
    }
  }, [documentType, reset]);

  const onFormSubmit = (data: DocumentType) => {
    onSubmit(data);
  };

  const categoryOptions = [
    { value: 'employee', label: 'Employee' },
    { value: 'car', label: 'Car' },
    { value: 'crew', label: 'Crew' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {documentType ? 'Edit Document Type' : 'Add New Document Type'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="space-y-6">
            <Input
              label="Document Name"
              {...register('name')}
              error={errors.name?.message}
              placeholder="Marine Passport"
            />
            
            <Select
              label="Category"
              {...register('category')}
              options={categoryOptions}
              error={errors.category?.message}
            />
            
            <Input
              label="Notification Days"
              type="number"
              {...register('notificationDays')}
              error={errors.notificationDays?.message}
              placeholder="30"
              min="1"
              max="365"
            />
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('isRequired')}
                className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                This document is required
              </label>
            </div>
            
            <TextArea
              label="Description"
              {...register('description')}
              error={errors.description?.message}
              placeholder="Brief description of the document"
              rows={3}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : documentType ? 'Update Document Type' : 'Add Document Type'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentTypeForm;